import { useInfiniteQuery } from 'react-query';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { TableBlockConfig } from '@reports/domain/value-objects/blocks/TableBlockConfig';
import type { TableBlockViewDto } from '@reports/application/dtos';
import { useReportsState } from '@reports/presentation/contexts/useReportsState';
import { buildReportBlockAnalytics } from '@reports/presentation/utils';
import { getBlocksUseCases } from '@reports/composition';

export interface UseInfiniteTableBlockProps {
  request: BlockDataRequestDto;
  blockConfig: TableBlockConfig;
  enabled?: boolean;
}

export interface UseInfiniteTableBlockReturn {
  tableBlockData: TableBlockViewDto | null;
  tableBlockError: Error | null;
  tableBlockState: {
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
    isFetchingNextPage: boolean;
  };
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  refetchTableBlock: () => void;
  removeQuery: () => void;
}

/**
 * Hook to fetch table block data with infinite scroll pagination
 *
 * Uses React Query's useInfiniteQuery to fetch and accumulate paginated data.
 * Automatically resets to page 1 when request parameters (filters) change.
 *
 * Features:
 * - Automatic page accumulation (all rows from all pages)
 * - Stops fetching when pagination.next is null
 * - Resets to top when filters change (new query key)
 * - Proper loading states for initial load and next page fetch
 * - Optimistic page number extraction from backend URL
 *
 * @param {UseInfiniteTableBlockProps} props - Hook configuration
 * @returns {UseInfiniteTableBlockReturn} Table data, state, and control functions
 *
 * @example
 * const { tableBlockData, tableBlockState, fetchNextPage, hasNextPage } = useInfiniteTableBlock({
 *   request: { startDate, endDate, filters: {...} },
 *   blockConfig: tableBlockConfig,
 *   enabled: true
 * });
 */
export const useInfiniteTableBlock = ({
  request,
  blockConfig,
  enabled = true,
}: UseInfiniteTableBlockProps): UseInfiniteTableBlockReturn => {
  const { fetchTableBlock } = getBlocksUseCases();
  const { state } = useReportsState();
  const { logEvent } = useAmplitude();
  const pageSize = blockConfig.pageSize || 10;

  // Query key includes request to trigger refetch when filters change
  // This ensures scroll resets to top on filter change
  const infiniteQuery = useInfiniteQuery<TableBlockViewDto, Error>(
    ['reports', 'block-infinite', blockConfig.key, request],
    async ({ pageParam = 1 }) => {
      const paginatedRequest: BlockDataRequestDto = {
        ...request,
        page: pageParam,
        pageSize,
      };
      return await fetchTableBlock.execute(paginatedRequest, blockConfig);
    },
    {
      enabled: enabled && blockConfig.paginationEnabled === true,
      refetchOnWindowFocus: false,
      retry: false,
      // Determine next page number from backend response
      getNextPageParam: (lastPage) => {
        // Stop fetching when next is null
        if (!lastPage.pagination.next) {
          return undefined;
        }

        // Extract page number from next URL
        // Backend returns: "/api/v1/reports/query/2?page=2&pageSize=10"
        const nextUrl = lastPage.pagination.next;
        const match = nextUrl.match(/page=(\d+)/);

        if (match) {
          return parseInt(match[1], 10);
        }

        // Fallback: couldn't parse, stop pagination
        return undefined;
      },
      // Log analytics only for the first page load
      onSuccess: (data) => {
        if (data.pages.length === 1) {
          const reportName = state.currentReport?.type || 'unknown';
          const analyticsData = buildReportBlockAnalytics(request, blockConfig.key, reportName);
          logEvent('@event.$reports.reportViewed', `@@section.${state.currentReport?.type}`, analyticsData);
        }
      },
    },
  );

  // Flatten all pages into single arrays for rendering
  const allRows = infiniteQuery.data?.pages.flatMap((page) => page.rows) || [];
  const columns = infiniteQuery.data?.pages[0]?.columns || [];
  const lastPage = infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1];

  return {
    tableBlockData:
      allRows.length > 0
        ? {
            columns: columns,
            rows: allRows,
            pagination: lastPage?.pagination || { next: null, prev: null },
          }
        : null,
    tableBlockError: infiniteQuery.error || null,
    tableBlockState: {
      isFetching: infiniteQuery.isFetching,
      isError: infiniteQuery.isError,
      isSuccess: infiniteQuery.isSuccess,
      isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    },
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    refetchTableBlock: infiniteQuery.refetch,
    removeQuery: infiniteQuery.remove,
  };
};
